import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ProductsAPI } from "../../api/products";
import { assetUrl } from "../../api/http";
import {
  Product,
  ProductCategory,
  ProductListResponse,
} from "../../types/products";

// ---------- utils ----------
const LIMIT_OPTIONS = [12, 20, 50] as const;

function formatMoney(n: number | undefined) {
  if (typeof n !== "number") return "-";
  return `$${n.toFixed(2)}`;
}
function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// ---------- styles ----------
const pageShell: React.CSSProperties = { display: "grid", gap: 16 };

const toolbarWrap: React.CSSProperties = {
  position: "sticky" as const,
  top: 0,
  zIndex: 1000,
  background: "rgba(255,255,255,0.98)",
  backdropFilter: "saturate(180%) blur(6px)",
  borderBottom: "1px solid #eee",
  padding: "10px 12px",
};

const toolbarRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 12,
  alignItems: "center",
};

const filters: React.CSSProperties = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
};

const input: React.CSSProperties = {
  padding: "10px 12px",
  border: "1px solid #ddd",
  borderRadius: 10,
  minWidth: 260,
  background: "#fff",
};

const selectCss: React.CSSProperties = {
  ...input,
  minWidth: 160,
};

const button: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  ...button,
  border: "1px solid #111",
  background: "#111",
  color: "#fff",
};

const outlineBtn: React.CSSProperties = {
  ...button,
  border: "1px solid #ddd",
  background: "#fff",
  color: "#111",
};

const tableOuter: React.CSSProperties = {
  overflowX: "auto",
  WebkitOverflowScrolling: "touch" as any,
};

const tableWrap: React.CSSProperties = {
  border: "1px solid #eee",
  borderRadius: 12,
  overflow: "hidden",
  background: "#fff",
  position: "relative",
  zIndex: 0,
  marginTop: 4,
};

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: 900,
};

const th: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 700,
  fontSize: 13,
  color: "#333",
  padding: "12px 14px",
  borderBottom: "1px solid #eee",
  background: "#fafafa",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  padding: "12px 14px",
  borderBottom: "1px solid #f1f1f1",
  verticalAlign: "middle",
  background: "#fff",
};

const thumb: React.CSSProperties = {
  width: 56,
  height: 56,
  objectFit: "cover" as const,
  borderRadius: 8,
  border: "1px solid #eee",
  background: "#fafafa",
};

const badge: React.CSSProperties = {
  display: "inline-block",
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "#f6f6f6",
  border: "1px solid #e8e8e8",
  whiteSpace: "nowrap",
};

const pillActive: React.CSSProperties = {
  ...badge,
  background: "#edfff1",
  borderColor: "#d7f5df",
};

const pillInactive: React.CSSProperties = {
  ...badge,
  background: "#fff3f3",
  borderColor: "#ffe0e0",
};

const footerBar: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const pagination: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

// ---------- component ----------
type FetchParams = {
  page?: number;
  limit?: number;
  q?: string;
  category?: ProductCategory;
  featured?: boolean;
};

export default function ProductsList() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  // --- Local state is source of truth ---
  const [q, setQ] = useState<string>(sp.get("q") ?? "");
  const [categoryStr, setCategoryStr] = useState<string>(
    sp.get("category") ?? ""
  );
  const [featuredStr, setFeaturedStr] = useState<string>(
    sp.get("featured") ?? ""
  );
  const [limitStr, setLimitStr] = useState<string>(
    sp.get("limit") ?? String(LIMIT_OPTIONS[0])
  );
  const [pageStr, setPageStr] = useState<string>(sp.get("page") ?? "1");

  // Keep URL in sync when local state changes
  useEffect(() => {
    const next = new URLSearchParams(sp);
    const setOrDel = (k: string, v: string) =>
      v ? next.set(k, v) : next.delete(k);
    setOrDel("q", q);
    setOrDel("category", categoryStr);
    setOrDel("featured", featuredStr);
    setOrDel("limit", limitStr);
    setOrDel("page", pageStr);
    if (next.toString() !== sp.toString()) setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, categoryStr, featuredStr, limitStr, pageStr]);

  // Sync from URL when it changes
  useEffect(() => {
    const fromUrl = {
      q: sp.get("q") ?? "",
      category: sp.get("category") ?? "",
      featured: sp.get("featured") ?? "",
      limit: sp.get("limit") ?? String(LIMIT_OPTIONS[0]),
      page: sp.get("page") ?? "1",
    };
    if (fromUrl.q !== q) setQ(fromUrl.q);
    if (fromUrl.category !== categoryStr) setCategoryStr(fromUrl.category);
    if (fromUrl.featured !== featuredStr) setFeaturedStr(fromUrl.featured);
    if (fromUrl.limit !== limitStr) setLimitStr(fromUrl.limit);
    if (fromUrl.page !== pageStr) setPageStr(fromUrl.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.toString()]);

  const page = Math.max(1, parseInt(pageStr || "1", 10));
  const limit = Math.max(1, parseInt(limitStr || String(LIMIT_OPTIONS[0]), 10));

  const category = useMemo(
    () =>
      (Object.values(ProductCategory) as string[]).includes(categoryStr)
        ? (categoryStr as ProductCategory)
        : undefined,
    [categoryStr]
  );

  const featured = useMemo(
    () =>
      featuredStr === "true"
        ? true
        : featuredStr === "false"
        ? false
        : undefined,
    [featuredStr]
  );

  const debouncedQ = useDebounced(q, 300);

  // data state
  const [data, setData] = useState<ProductListResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingFlagId, setTogglingFlagId] = useState<string | null>(null);

  // fetch
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const params: FetchParams = {
          page,
          limit,
          q: debouncedQ || undefined,
          category,
          featured,
        };
        const resp = await ProductsAPI.list(params);
        if (!ignore) setData(resp);
      } catch (e: any) {
        if (!ignore) setErr(e?.message || "Failed to load products");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [page, limit, debouncedQ, category, featured]);

  // helpers
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const goPage = (p: number) =>
    setPageStr(String(Math.min(Math.max(1, p), totalPages)));

  // delete
  const onDelete = async (id: string) => {
    const name = items.find((x) => x._id === id)?.name || "this product";
    const ok = window.confirm(`Delete "${name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      setDeletingId(id);
      await ProductsAPI.remove(id);
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.filter((p) => p._id !== id),
              total: Math.max(0, prev.total - 1),
            }
          : prev
      );
    } finally {
      setDeletingId(null);
    }
  };

  const onQuickToggleFlags = async (
    product: Product,
    next: Partial<Pick<Product, "isActive" | "featured" | "topSeller">>
  ) => {
    const id = String(product._id || "").trim();
    if (!id) return;
    const previousFlags = {
      isActive: Boolean(product.isActive),
      featured: Boolean(product.featured),
    };
    setTogglingFlagId(id);
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((item) =>
              item._id === id ? { ...item, ...next } : item
            ),
          }
        : prev
    );
    try {
      await ProductsAPI.patch(id, next as Record<string, unknown>);
    } catch (error: any) {
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item._id === id
                  ? {
                      ...item,
                      isActive: previousFlags.isActive,
                      featured: previousFlags.featured,
                    }
                  : item
              ),
            }
          : prev
      );
      window.alert(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update product flags."
      );
    } finally {
      setTogglingFlagId((current) => (current === id ? null : current));
    }
  };

  const ThumbCell = ({ p }: { p: Product }) => {
    const u =
      assetUrl((p as any).heroImage) ||
      assetUrl(p.previewImage) ||
      assetUrl((p.moreImages || [])[0]);
    return u ? (
      <img src={u} alt={p.name} style={thumb} />
    ) : (
      <div
        style={{
          ...thumb,
          display: "grid",
          placeItems: "center",
          color: "#bbb",
          fontSize: 12,
        }}
      >
        —
      </div>
    );
  };

  return (
    <div style={pageShell}>
      {/* Responsive tweaks via scoped CSS */}
      <style>{`
        @media (max-width: 768px) {
          .pl-toolbar-row { grid-template-columns: 1fr !important; }
          .pl-filters input, .pl-filters select { min-width: 0 !important; width: 100% !important; }
          .pl-filters { gap: 6px !important; }
          .pl-primary-btn { width: 100%; }
          .pl-table-wrap { display: none !important; }
          .pl-mobile-list { display: grid !important; }
          .pl-mobile-actions { display: none !important; }
        }
        @media (min-width: 769px) {
          .pl-table-wrap { display: block !important; }
          .pl-mobile-list { display: none !important; }
          .pl-mobile-actions { display: none !important; } /* only show extra action row on mobile */
        }
      `}</style>

      {/* toolbar */}
      <div style={toolbarWrap}>
        <div className="pl-toolbar-row" style={toolbarRow}>
          <div className="pl-filters" style={filters}>
            <input
              type="search"
              placeholder="Search name / slug / tags…"
              style={input}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPageStr("1");
              }}
            />

            <select
              style={selectCss}
              value={categoryStr}
              onChange={(e) => {
                setCategoryStr(e.target.value);
                setPageStr("1");
              }}
            >
              <option value="">All categories</option>
              {Object.values(ProductCategory).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              style={selectCss}
              value={featuredStr}
              onChange={(e) => {
                setFeaturedStr(e.target.value);
                setPageStr("1");
              }}
            >
              <option value="">Any featured</option>
              <option value="true">Featured only</option>
              <option value="false">Not featured</option>
            </select>

            <select
              style={{ ...selectCss, width: 140 }}
              value={String(limit)}
              onChange={(e) => {
                setLimitStr(e.target.value);
                setPageStr("1");
              }}
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Link
              to="/products/new"
              className="pl-primary-btn"
              style={{ ...primaryBtn, textDecoration: "none" }}
            >
              + New Product
            </Link>
          </div>
        </div>
      </div>

      {/* table (with mobile-friendly extra action row) */}
      <div className="pl-table-wrap" style={tableOuter}>
        <div style={tableWrap}>
          <table className="pl-table" style={table}>
            <thead>
              <tr>
                <th style={{ ...th, width: 72 }}>Image</th>
                <th style={th}>Name</th>
                <th style={th}>Category</th>
                <th style={th}>Price</th>
                <th style={th}>Stock</th>
                <th style={th}>Flags</th>
                <th
                  className="pl-hide-mobile"
                  style={{
                    ...th,
                    width: 260,
                    textAlign: "right",
                    position: "sticky",
                    right: 0,
                    zIndex: 2,
                    boxShadow: "-10px 0 18px rgba(15,23,42,0.06)",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td style={td} colSpan={7}>
                    Loading…
                  </td>
                </tr>
              ) : err ? (
                <tr>
                  <td style={{ ...td, color: "#b00" }} colSpan={7}>
                    {err}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td style={td} colSpan={7}>
                    No products found. Try adjusting your filters or{" "}
                    <Link to="/products/new">create your first product</Link>.
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <React.Fragment key={p._id}>
                    <tr>
                      <td style={td}>
                        <ThumbCell p={p} />
                      </td>
                      <td style={{ ...td, maxWidth: 360 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {p.name}
                        </div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          {p.slug}
                        </div>
                      </td>
                      <td style={td}>
                        <span style={badge}>{p.category}</span>
                      </td>
                      <td style={td}>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "baseline",
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontWeight: 700 }}>
                            {formatMoney(p.price)}
                          </span>
                          {typeof p.compareAtPrice === "number" &&
                          p.compareAtPrice > p.price ? (
                            <span
                              style={{
                                textDecoration: "line-through",
                                color: "#888",
                                fontSize: 12,
                              }}
                            >
                              {formatMoney(p.compareAtPrice)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td style={td}>{p.stockCount}</td>
                      <td style={td}>
                        <div
                          style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
                        >
                          {p.featured ? (
                            <span
                              style={{
                                ...badge,
                                background: "#eef6ff",
                                borderColor: "#d9eaff",
                              }}
                            >
                              Featured
                            </span>
                          ) : null}
                          {p.topSeller ? (
                            <span
                              style={{
                                ...badge,
                                background: "#ecfdf5",
                                borderColor: "#bbf7d0",
                                color: "#166534",
                              }}
                            >
                              Top seller
                            </span>
                          ) : null}
                          {p.isActive ? (
                            <span style={pillActive}>Active</span>
                          ) : (
                            <span style={pillInactive}>Inactive</span>
                          )}
                        </div>
                        <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                              color: "#334155",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(p.isActive)}
                              disabled={togglingFlagId === p._id}
                              onChange={(e) =>
                                void onQuickToggleFlags(p, {
                                  isActive: e.target.checked,
                                })
                              }
                            />
                            Active
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                              color: "#334155",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(p.featured)}
                              disabled={togglingFlagId === p._id}
                              onChange={(e) =>
                                void onQuickToggleFlags(p, {
                                  featured: e.target.checked,
                                })
                              }
                            />
                            Featured
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                              color: "#334155",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(p.topSeller)}
                              disabled={togglingFlagId === p._id}
                              onChange={(e) =>
                                void onQuickToggleFlags(p, {
                                  topSeller: e.target.checked,
                                })
                              }
                            />
                            Top seller
                          </label>
                        </div>
                      </td>
                      <td
                        className="pl-hide-mobile"
                        style={{
                          ...td,
                          textAlign: "right",
                          position: "sticky",
                          right: 0,
                          zIndex: 1,
                          boxShadow: "-10px 0 18px rgba(15,23,42,0.06)",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-grid",
                            gridTemplateColumns: "auto auto auto",
                            gap: 8,
                            justifyContent: "end",
                          }}
                        >
                          <Link
                            to={`/products/${p._id}`}
                            style={{
                              ...outlineBtn,
                              textDecoration: "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            onClick={() => nav(`/products/${p._id}`)}
                            style={{ ...outlineBtn, whiteSpace: "nowrap" }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === p._id}
                            onClick={() => onDelete(p._id!)}
                            style={{
                              ...button,
                              border: "1px solid #e74c3c",
                              background:
                                deletingId === p._id ? "#f4d7d5" : "#fff",
                              color: "#e74c3c",
                              whiteSpace: "nowrap",
                            }}
                            title="Delete product"
                          >
                            {deletingId === p._id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Mobile-only extra action row */}
                    <tr className="pl-mobile-actions">
                      <td style={td} colSpan={7}>
                        <div
                          className="pl-actions"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "auto auto auto",
                            gap: 8,
                          }}
                        >
                          <Link
                            to={`/products/${p._id}`}
                            style={{ ...outlineBtn, textDecoration: "none" }}
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            onClick={() => nav(`/products/${p._id}`)}
                            style={{ ...outlineBtn }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === p._id}
                            onClick={() => onDelete(p._id!)}
                            style={{
                              ...button,
                              border: "1px solid #e74c3c",
                              background:
                                deletingId === p._id ? "#f4d7d5" : "#fff",
                              color: "#e74c3c",
                            }}
                            title="Delete product"
                          >
                            {deletingId === p._id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pl-mobile-list" style={{ display: "none", gap: 12 }}>
        {loading ? (
          <div style={{ ...tableWrap, padding: 14, color: "#666" }}>Loading…</div>
        ) : err ? (
          <div style={{ ...tableWrap, padding: 14, color: "#b00" }}>{err}</div>
        ) : items.length === 0 ? (
          <div style={{ ...tableWrap, padding: 14, color: "#666" }}>
            No products found. Try adjusting your filters or{" "}
            <Link to="/products/new">create your first product</Link>.
          </div>
        ) : (
          items.map((p) => (
            <article
              key={p._id}
              style={{
                border: "1px solid #eee",
                borderRadius: 12,
                background: "#fff",
                padding: 12,
                display: "grid",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px minmax(0, 1fr)",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                <ThumbCell p={p} />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#111",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.name}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.slug}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <div>
                  <div style={{ color: "#666" }}>Category</div>
                  <div style={{ marginTop: 3, fontWeight: 800 }}>{p.category}</div>
                </div>
                <div>
                  <div style={{ color: "#666" }}>Price</div>
                  <div style={{ marginTop: 3, fontWeight: 800 }}>
                    {formatMoney(p.price)}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#666" }}>Stock</div>
                  <div style={{ marginTop: 3, fontWeight: 800 }}>{p.stockCount}</div>
                </div>
                <div>
                  <div style={{ color: "#666" }}>Status</div>
                  <div style={{ marginTop: 3 }}>
                    {p.isActive ? (
                      <span style={pillActive}>Active</span>
                    ) : (
                      <span style={pillInactive}>Inactive</span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                {[
                  ["Active", "isActive"],
                  ["Featured", "featured"],
                  ["Top seller", "topSeller"],
                ].map(([label, key]) => (
                  <label
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      color: "#334155",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean((p as any)[key])}
                      disabled={togglingFlagId === p._id}
                      onChange={(e) =>
                        void onQuickToggleFlags(p, {
                          [key]: e.target.checked,
                        } as any)
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 8,
                }}
              >
                <Link
                  to={`/products/${p._id}`}
                  style={{ ...outlineBtn, textAlign: "center", textDecoration: "none" }}
                >
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={() => nav(`/products/${p._id}`)}
                  style={outlineBtn}
                >
                  View
                </button>
                <button
                  type="button"
                  disabled={deletingId === p._id}
                  onClick={() => onDelete(p._id!)}
                  style={{
                    ...button,
                    border: "1px solid #e74c3c",
                    background: deletingId === p._id ? "#f4d7d5" : "#fff",
                    color: "#e74c3c",
                  }}
                  title="Delete product"
                >
                  {deletingId === p._id ? "Deleting…" : "Delete"}
                </button>
              </div>
            </article>
          ))
        )}
      </div>

      {/* footer / pagination */}
      <div style={footerBar}>
        <div style={{ color: "#666", fontSize: 13 }}>
          Page {page} of {Math.max(1, Math.ceil(total / limit))} • {total} total
        </div>
        <div style={pagination}>
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => goPage(page - 1)}
            style={{ ...outlineBtn, opacity: canPrev ? 1 : 0.5 }}
          >
            ◀ Prev
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => goPage(page + 1)}
            style={{ ...outlineBtn, opacity: canNext ? 1 : 0.5 }}
          >
            Next ▶
          </button>
        </div>
      </div>
    </div>
  );
}
